import React, { useEffect, useMemo, useState, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Menu, Search, ShoppingCart, Home, ChevronRight, User, X,
  Plus, Minus, ShoppingBag, MapPin, Globe,
  ChevronDown, ChevronUp, RefreshCw, Tag, Package, ArrowRight,
  SlidersHorizontal, Star, ShoppingBasket,
} from "lucide-react";

import logo from "../../assets/app/logo.png";

import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import {
  listenToPublishedProducts,
  filterLocalProductsByCountry,
  setFilters,
} from "../../redux/productSlice";
import { fetchAllProductSummaries } from "../../redux/productRatingSlice";

import { CurrencyContext } from "../../GLOBAL/CurrencyContext";
import { Notifications, SupportAgent, ChatBubble } from "@mui/icons-material";
import {
  listenToUserNotifications,
  stopUserNotificationsListener,
} from "../../redux/notificationsSlice";
import { electronicsColorAttributes, electronicsSizeAttributes } from "../../UNIVERSAL/ATTRIBUTES/ElectronicsAttributes";
import { electronicsCategories, electronicsSubcategories, electronicsProducts } from "../../UNIVERSAL/CATEGORIES/ElectronicsCategories";
import { addToCart, decrementCartItem, clearCart } from "../../redux/cartSlice";
import { fetchAllCommercePartners } from "../../redux/commercePartnersSlice";

/* ═══════════════════════════════════════════════════════════
   DESIGN TOKENS — dark neon blue→purple→pink
   const T = {
  pageBg:     "#08060F",
  cardBg:     "#100D1C",
  cardBg2:    "#13102A",
  surfaceBg:  "#1A1530",
  grad:       "linear-gradient(90deg, #007BFF 0%, #6A00FF 50%, #FF00D4 100%)",
  gradAnim:   "linear-gradient(90deg, #007BFF, #6A00FF, #FF00D4, #6A00FF, #007BFF)",
  blue:       "#007BFF",
  purple:     "#6A00FF",
  pink:       "#FF00D4",
  white:      "#FFFFFF",
  offwhite:   "#E8E4FF",
  muted:      "#8B82A8",
  mutedDim:   "#5A5278",
  border:     "rgba(106,0,255,0.25)",
  gold:       "#FFD60A",
  red:        "#FF3B5C",
};
═══════════════════════════════════════════════════════════ */
const T = {
  pageBg:     "#F8F7FF",
  cardBg:     "#FFFFFF",
  cardBg2:    "#F3F0FF",
  surfaceBg:  "#EDE9FF",
  grad:       "linear-gradient(90deg, #007BFF 0%, #6A00FF 50%, #FF00D4 100%)",
  gradAnim:   "linear-gradient(90deg, #007BFF, #6A00FF, #FF00D4, #6A00FF, #007BFF)",
  blue:       "#007BFF",
  purple:     "#6A00FF",
  pink:       "#FF00D4",
  white:      "#1A1040",
  offwhite:   "#2D1F6E",
  muted:      "#7B6FA0",
  mutedDim:   "#B0A8CC",
  border:     "rgba(106,0,255,0.15)",
  gold:       "#D4A500",
  red:        "#FF3B5C",
};

const GRAD   = T.grad;
const GRAD_A = T.gradAnim;

// Minimal country → continent map. Extend as needed.
const COUNTRY_TO_CONTINENT: Record<string, string> = {
  estonia: "Europe", latvia: "Europe", lithuania: "Europe", finland: "Europe",
  sweden: "Europe", norway: "Europe", denmark: "Europe", germany: "Europe",
  france: "Europe", spain: "Europe", italy: "Europe", portugal: "Europe",
  poland: "Europe", "united kingdom": "Europe", ireland: "Europe",
  netherlands: "Europe", belgium: "Europe", austria: "Europe",
  switzerland: "Europe", greece: "Europe", "czech republic": "Europe",
  hungary: "Europe", romania: "Europe", croatia: "Europe", iceland: "Europe",

  "united states": "North America", canada: "North America", mexico: "North America",

  brazil: "South America", argentina: "South America", chile: "South America",
  peru: "South America", colombia: "South America",

  nigeria: "Africa", kenya: "Africa", "south africa": "Africa", egypt: "Africa",
  morocco: "Africa", ghana: "Africa", tanzania: "Africa", ethiopia: "Africa",

  china: "Asia", japan: "Asia", india: "Asia", thailand: "Asia",
  "united arab emirates": "Asia", "south korea": "Asia", indonesia: "Asia",
  vietnam: "Asia", "saudi arabia": "Asia", israel: "Asia", turkey: "Asia",

  australia: "Oceania", "new zealand": "Oceania", fiji: "Oceania",
};

const getContinent = (country?: string | null): string | null => {
  if (!country) return null;
  return COUNTRY_TO_CONTINENT[country.toLowerCase().trim()] ?? null;
};

/* ═══════════════════════════════════════════════════════════
   GLOBAL CSS
═══════════════════════════════════════════════════════════ */
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,400&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
  ::-webkit-scrollbar{display:none;}
  input,button{font-family:inherit;}

  @keyframes gradSpin{0%{background-position:0% 50%;}50%{background-position:100% 50%;}100%{background-position:0% 50%;}}
  @keyframes mc-ticker{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}
  @keyframes mc-pulse{0%,100%{opacity:1}50%{opacity:.3}}

  input::placeholder{color:rgba(255,255,255,.3);}

  /* card hover lift with neon glow */
  .p-card{transition:box-shadow .22s,transform .22s;}
  .p-card:hover{
    box-shadow:0 0 0 1.5px rgba(107,0,255,.6),0 12px 40px rgba(107,0,255,.22),0 0 0 0.5px rgba(255,0,212,.25);
    transform:translateY(-4px);
  }

  /* animated gradient price pill */
  .price-pill{
    display:inline-flex;align-items:center;
    background:linear-gradient(90deg,#007BFF,#6A00FF,#FF00D4);
    background-size:200% 100%;animation:gradSpin 3s ease infinite;
    border-radius:999px;padding:4px 14px;
    font-weight:900;color:#fff;letter-spacing:-0.4px;
    box-shadow:0 2px 12px rgba(106,0,255,.45);
  }

  /* add button */
  .add-btn{
    border-radius:50%;border:none;cursor:pointer;
    background:linear-gradient(135deg,#007BFF,#FF00D4);
    display:flex;align-items:center;justify-content:center;
    box-shadow:0 3px 14px rgba(255,0,212,.45);
    transition:transform .18s,box-shadow .18s;
  }
  .add-btn:hover{transform:scale(1.12);box-shadow:0 6px 22px rgba(106,0,255,.65);}
  .add-btn:active{transform:scale(0.88);}

  /* nav dropdown item */
  .nav-item{
    display:flex;align-items:center;gap:12px;padding:13px 18px;cursor:pointer;
    font-size:14px;font-weight:700;color:${T.offwhite};
    background:none;width:100%;text-align:left;
    border:none;border-bottom:1px solid rgba(106,0,255,.12);
    transition:background .15s;
  }
  .nav-item:hover{background:rgba(106,0,255,.15);}

  /* sort dropdown item */
  .sort-item{
    padding:11px 18px;font-size:13px;cursor:pointer;
    display:flex;align-items:center;justify-content:space-between;
    border-bottom:1px solid rgba(106,0,255,.12);
    color:${T.offwhite};font-weight:500;transition:background .15s;
  }
  .sort-item:hover{background:rgba(106,0,255,.18);}
  .sort-item.active{color:#007BFF;font-weight:800;}

  /* mobile bottom tab */
  .tab-item{display:flex;flex-direction:column;align-items:center;gap:3px;font-size:10px;font-weight:700;cursor:pointer;color:${T.mutedDim};padding:4px 10px;transition:color .2s;}
  .tab-item.active{color:#6A00FF;}
`;

/* ═══════════════════════════════════════════════════════════
   SIDEBAR STYLES
═══════════════════════════════════════════════════════════ */
const A: Record<string, React.CSSProperties> = {
  commerceCard:   { borderRadius: 18, padding: 18, background: T.cardBg, marginBottom: 16, boxShadow: "0 2px 24px rgba(106,0,255,.12)", border: "1.5px solid rgba(106,0,255,.22)" },
  commerceHeader: { fontSize: 16, fontWeight: 900, display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", color: T.offwhite },
  category:       { padding: 10, borderRadius: 12, background: "rgba(106,0,255,.07)", marginTop: 8, border: "1px solid rgba(106,0,255,.14)", cursor: "pointer" },
  row:            { display: "flex", alignItems: "center", gap: 10, fontWeight: 700, color: T.offwhite },
  sub:            { paddingLeft: 22, paddingTop: 8 },
  productGroup:   { paddingLeft: 42, paddingTop: 7, paddingBottom: 4, fontWeight: 600, color: T.muted, cursor: "pointer", fontSize: 13 },
  sidebar:        { background: T.cardBg, borderRadius: 20, padding: 20, boxShadow: "0 4px 32px rgba(106,0,255,.14)", position: "sticky" as const, top: 116, maxHeight: "calc(100vh - 130px)", overflowY: "auto" as const, border: "1.5px solid rgba(106,0,255,.22)" },
};

/* sort opts */
type SortKey = "default" | "price_asc" | "price_desc" | "newest" | "name_asc";
const SORT_OPTS: { key: SortKey; label: string }[] = [
  { key: "default",    label: "Best Match"       },
  { key: "newest",     label: "New Arrivals"      },
  { key: "price_asc",  label: "Price: Low → High" },
  { key: "price_desc", label: "Price: High → Low" },
  { key: "name_asc",   label: "Name: A → Z"       },
];

/* category → route map */
const CATEGORY_ROUTES: Record<number, string> = {
  20000001:"/Phones",20000020:"/Tablets",20000002:"/Computers",20000021:"/Laptops",
  20000003:"/Televisions",20000022:"/HomeEntertainment",20000004:"/Audio",20000005:"/Cameras",
  20000023:"/Film",20000006:"/Gaming",20000007:"/HomeAppliances",20000008:"/KitchenElectronics",
  20000009:"/Networking",20000024:"/SmartHome",20000010:"/CarElectronics",20000011:"/Tools",
  20000025:"/Accessories",20000012:"/OfficeElectronics",20000014:"/Security",20000015:"/Wearables",
  20000016:"/GPS",20000017:"/eBookReaders",
};

/* page layout styles */
const S: Record<string, React.CSSProperties> = {
  page:        { minHeight: "100vh", background: T.pageBg, paddingBottom: 90, fontFamily: "'Plus Jakarta Sans',sans-serif" },
  headerTop:   { background: T.cardBg, position: "sticky", top: 0, zIndex: 200, boxShadow: "0 2px 32px rgba(106,0,255,.22)", borderBottom: "1px solid rgba(106,0,255,.18)" },
  headerRow1:  { display: "flex", alignItems: "center", gap: 10, padding: "12px 16px 8px" },
  headerRow2:  { display: "flex", alignItems: "center", gap: 8, padding: "0 16px 10px", overflowX: "auto", scrollbarWidth: "none" as any },
  searchWrap:  { display: "flex", alignItems: "center", flex: 1, background: "rgba(255,255,255,.06)", borderRadius: 999, overflow: "hidden", border: "1.5px solid rgba(106,0,255,.35)", transition: "border-color .2s,box-shadow .2s" },
  searchInput: { flex: 1, border: "none", outline: "none", padding: "10px 16px", fontSize: 14, color: "#fff", background: "transparent", fontFamily: "inherit" },
  searchBtn:   { width: 44, minHeight: 42, background: GRAD, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, borderRadius: "0 999px 999px 0" },
  hBtn:        { display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,.06)", border: "1px solid rgba(106,0,255,.3)", cursor: "pointer", color: T.offwhite, fontSize: 12, fontWeight: 700, padding: "7px 13px", borderRadius: 999, flexShrink: 0, whiteSpace: "nowrap" as const, transition: "background .2s" },
  navOverlay:  { position: "fixed" as const, inset: 0, zIndex: 599 },
  navMenu:     { position: "fixed" as const, top: 72, right: 16, width: 230, zIndex: 600, background: T.cardBg, borderRadius: 18, boxShadow: "0 24px 64px rgba(0,0,0,.5),0 0 0 1px rgba(106,0,255,.25)", overflow: "hidden" },
  sortMenu:    { position: "fixed" as const, background: T.cardBg, borderRadius: 14, boxShadow: "0 20px 56px rgba(0,0,0,.6),0 0 0 1px rgba(106,0,255,.25)", zIndex: 9999, minWidth: 210, overflow: "hidden" },
  desktopGrid: { display: "grid", gap: 24, padding: "24px 24px 0" },
  grid:        { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))", gap: 16 },
  mobileGrid:  { display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 12 },
  mobileTabs:  { position: "fixed" as const, bottom: 0, left: 0, right: 0, height: 64, background: T.cardBg, display: "flex", justifyContent: "space-around", alignItems: "center", boxShadow: "0 -4px 24px rgba(106,0,255,.18)", zIndex: 100, borderTop: "1px solid rgba(106,0,255,.2)" },
};

/* ═══════════════════════════════════════════════════════════
   GLOBAL TICKER
═══════════════════════════════════════════════════════════ */
const GlobalTicker: React.FC<{ denied: boolean }> = ({ denied }) => {
  const msg = denied
    ? "⚠ LOCATION ACCESS DENIED — SHOWING ALL GLOBAL PRODUCTS · ENABLE LOCATION TO SEE LOCAL LISTINGS · "
    : "◉ GLOBAL MODE ACTIVE — BROWSING WORLDWIDE INVENTORY · TAP LOCAL TO FILTER BY YOUR REGION · ";
  return (
    <div style={{ overflow:"hidden", background: denied ? "rgba(255,59,92,.10)" : "rgba(106,0,255,.10)", borderBottom:`1px solid ${denied?"rgba(255,59,92,.25)":"rgba(106,0,255,.25)"}`, height:22, display:"flex", alignItems:"center" }}>
      <span style={{ display:"inline-block", whiteSpace:"nowrap", animation:"mc-ticker 24s linear infinite", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:9, fontWeight:700, letterSpacing:".18em", color: denied ? T.red : T.purple, paddingLeft:24 }}>{msg.repeat(6)}</span>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
const ProductsHome: React.FC = () => {
  const dispatch   = useDispatch<AppDispatch>();
  const navigate   = useNavigate();
  const { rate, symbol } = useContext(CurrencyContext);

  const authUser      = useSelector((s: RootState) => s.auth.user);
  const authLoaded    = useSelector((s: RootState) => s.auth.authLoaded);
  const userId        = authUser?.userId;
  const notifications = useSelector((s: RootState) => s.notifications.notifications);
  const commercePartners = useSelector((s: RootState) => s.commercePartners.partners);
const brandMap = useMemo(() => {
  const m = new Map<string, any>();
  (commercePartners || []).forEach((p: any) => m.set(p.brandId, p));
  return m;
}, [commercePartners]);
  
  const unreadCount   = useMemo(() => notifications.filter((n: any) => !n.read).length, [notifications]);

  /* products from productSlice */
  const listings         = useSelector((s: RootState) => s.products.products);
  const filteredListings = useSelector((s: RootState) => s.products.filteredProducts);
  const cartItems        = useSelector((s: RootState) => s.cart.items);
  const cartTotal        = cartItems.reduce((s: number, i: any) => s + i.qty, 0);

  /* rating summaries from productRatingSlice */
  const ratingSummaries = useSelector((s: RootState) => s.productRatings.summaries);

  const [isMobile,           setIsMobile]          = useState(window.innerWidth <= 768);
  const [catOpen,            setCatOpen]           = useState(false);
  const [desktopCatOpen,     setDesktopCatOpen]    = useState(false);
  const [navOpen,            setNavOpen]           = useState(false);
  const [searchQuery,        setSearchQuery]       = useState("");
  const [searchFocused,      setSearchFocused]     = useState(false);
  const [sortOpen,           setSortOpen]          = useState(false);
  const [activeSort,         setActiveSort]        = useState<SortKey>("default");
  const [activeProductGroup, setActiveProductGroup]= useState<number | null>(null);
  const [openCategories,     setOpenCategories]    = useState<number | null>(null);
  const [openSubCategories,  setOpenSubCategories] = useState<number | null>(null);
  const sortBtnRef = useRef<HTMLButtonElement>(null);
  const [sortAnchor, setSortAnchor] = useState<{ top: number; left: number } | null>(null);

  const [userCountry,       setUserCountry]       = useState<string | null>(null);
  const [userCity,          setUserCity]          = useState<string | null>(null);
  const [userLat,           setUserLat]           = useState<number | null>(null);
  const [userLng,           setUserLng]           = useState<number | null>(null);
  const [locationDenied,    setLocationDenied]    = useState(false);
  const [locationLoading,   setLocationLoading]   = useState(true);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [showGlobal,        setShowGlobal]        = useState(false);
  const locationActive = !locationDenied && userLat !== null;

  const [selectedColor, setSelectedColor] = useState<number | null>(null);
  const [selectedSize,  setSelectedSize]  = useState<number | null>(null);
  const [variantQty,    setVariantQty]    = useState(1);
  const [variantOpen,   setVariantOpen]   = useState(false);
  const [variantItem,   setVariantItem]   = useState<any | null>(null);
  const [variantMode,   setVariantMode]   = useState<"fashion" | "beauty" | null>(null);

  useEffect(() => { dispatch(fetchAllCommercePartners()); }, [dispatch]);

const brandTier = useMemo(() => {
  const tiers = new Map<string, number>();
  const deviceCity = userCity?.toLowerCase().trim();
  const deviceCountry = userCountry?.toLowerCase().trim();
  const deviceContinent = getContinent(userCountry);

  (commercePartners || []).forEach((p: any) => {
    const brandCity = p.address?.city?.toLowerCase().trim();
    const brandCountry = p.address?.country?.toLowerCase().trim();
    const brandContinent = getContinent(p.address?.country);

    let tier = 3;
    if (deviceCity && brandCity && brandCity === deviceCity) tier = 0;
    else if (deviceCountry && brandCountry && brandCountry === deviceCountry) tier = 1;
    else if (deviceContinent && brandContinent && brandContinent === deviceContinent) tier = 2;

    tiers.set(p.brandId, tier);
  });

  return tiers;
}, [commercePartners, userCity, userCountry]);
  const [visibleIds, setVisibleIds] = useState<{
    categories: Set<number>; subs: Set<number>; products: Set<number>;
  }>({ categories: new Set(), subs: new Set(), products: new Set() });





  /* ── resize listener ── */
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    fn(); window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  /* ── auth guard ── */
  useEffect(() => {
    if (!authLoaded) return;
    if (!authUser || !userId) navigate("/");
  }, [authLoaded, authUser, userId, navigate]);

  /* ── data listeners ── */
  useEffect(() => { dispatch(listenToPublishedProducts()); }, [dispatch]);
  useEffect(() => { dispatch(fetchAllProductSummaries()); }, [dispatch]);
  useEffect(() => { dispatch(fetchAllCommercePartners()); }, [dispatch]);


  useEffect(() => {
    if (!userId) return;
    dispatch(listenToUserNotifications(userId));
    return () => { dispatch(stopUserNotificationsListener()); };
  }, [dispatch, userId]);

  /* ── build category visibility from live listings ── */
  useEffect(() => {
    if (!listings.length) { setVisibleIds({ categories: new Set(), subs: new Set(), products: new Set() }); return; }
    const categories = new Set<number>(), subs = new Set<number>(), products = new Set<number>();
    listings.forEach((l: any) => {
      if (l.categoryId)    categories.add(l.categoryId);
      if (l.subcategoryId) subs.add(l.subcategoryId);
      if (l.productId)     products.add(l.productId);
    });
    setVisibleIds({ categories, subs, products });
  }, [listings]);

  /* ── geolocation ── */
  useEffect(() => {
    if (!navigator.geolocation) { setLocationDenied(true); setLocationLoading(false); dispatch(filterLocalProductsByCountry(null)); return; }
    const detect = () => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserLat(pos.coords.latitude); setUserLng(pos.coords.longitude); setLocationDenied(false);
          if ((window as any).google?.maps) {
            const gc = new (window as any).google.maps.Geocoder();
            gc.geocode({ location: { lat: pos.coords.latitude, lng: pos.coords.longitude } }, (results: any, status: string) => {
              if (status === "OK" && results?.[0]) {
                const comps = results[0].address_components;
                const get   = (t: string) => comps.find((c: any) => c.types.includes(t))?.long_name ?? null;
                setUserCity(get("locality") || get("administrative_area_level_2"));
                const country = get("country"); setUserCountry(country);
                if (country) dispatch(filterLocalProductsByCountry(country));
              }
              setLocationLoading(false);
            });
          } else { setLocationLoading(false); }
        },
        (err) => {
          if (err.code === err.PERMISSION_DENIED) setLocationDenied(true);
          setLocationLoading(false); dispatch(filterLocalProductsByCountry(null));
        }
      );
    };
    detect();
    if ("permissions" in navigator) {
      navigator.permissions.query({ name: "geolocation" as PermissionName }).then(r => {
        r.onchange = () => { if (r.state === "granted") detect(); };
      });
    }
  }, [dispatch]);

  useEffect(() => {
    if (!userCountry) return;
    dispatch(filterLocalProductsByCountry(showGlobal ? null : userCountry));
  }, [userCountry, showGlobal, dispatch]);

  const handleToggleGlobal = () => {
    const next = !showGlobal; setShowGlobal(next);
    setActiveProductGroup(null);
    dispatch(setFilters({ city: null, country: null }));
    dispatch(filterLocalProductsByCountry(next ? null : userCountry));
  };

  const toggleSort = () => {
    if (sortOpen) { setSortOpen(false); setSortAnchor(null); return; }
    if (sortBtnRef.current) {
      const r = sortBtnRef.current.getBoundingClientRect();
      setSortAnchor({ top: r.bottom + 6, left: r.left });
    }
    setSortOpen(true);
  };

  useEffect(() => { if (searchQuery.trim()) setActiveProductGroup(null); }, [searchQuery]);

  const closePanel   = () => { setCatOpen(false); setDesktopCatOpen(false); };

  const resetFilters = () => {
    setActiveProductGroup(null); setActiveSort("default");
    dispatch(setFilters({ city: null, country: null }));
    dispatch(filterLocalProductsByCountry(showGlobal ? null : userCountry));
    dispatch(listenToPublishedProducts());
  };

  /* ── variant helpers ── */
  const getVariantMode = (item: any): "fashion" | "beauty" | null => {
    if (!item.variants || !Object.keys(item.variants).length) return null;
    if (Object.values(item.variants).some((v: any) => Array.isArray(v.sizeIds) && v.sizeIds.length)) return "fashion";
    if (Object.values(item.variants).some((v: any) => v.imageUrl && (!v.sizeIds || !v.sizeIds.length))) return "beauty";
    return null;
  };

  const canContinue =
    variantMode === null ||
    (variantMode === "fashion" && selectedColor !== null && selectedSize !== null && variantQty > 0) ||
    (variantMode === "beauty"  && selectedColor !== null && variantQty > 0);

  const previewColorId = useMemo(() => {
    if (!variantItem || !variantMode) return null;
    const colors = variantItem.attributes?.[variantMode === "beauty" ? 10001 : 20002];
    if (!Array.isArray(colors) || !colors.length) return null;
    return selectedColor ?? colors[0];
  }, [variantItem, variantMode, selectedColor]);

  useEffect(() => {
    if (variantOpen && variantMode === "beauty" && variantItem && selectedColor === null) {
      const colors = variantItem.attributes?.[10001];
      if (Array.isArray(colors) && colors.length) setSelectedColor(colors[0]);
    }
  }, [variantOpen, variantMode, variantItem, selectedColor]);

  const openProductModal = (item: any) => {
    setVariantItem(item); setVariantMode(getVariantMode(item));
    setSelectedColor(null); setSelectedSize(null); setVariantQty(1); setVariantOpen(true);
  };

  const handleAddToCart = (item: any, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (getVariantMode(item) !== null) { openProductModal(item); return; }
    ensureCartBrandCompat(item.brandId);
    dispatch(addToCart({ listingId: item.listingId, name: item.name!, price: item.rate!, photo: item.photos?.[0], hostId: item.hostId, brandId: item.brandId }));
  };

  const handleVariantContinue = (toCheckout: boolean) => {
    if (!variantItem) return;
    ensureCartBrandCompat(variantItem.brandId);
    dispatch(addToCart({ listingId: variantItem.listingId, name: variantItem.name!, price: variantItem.rate!, photo: variantItem.variants?.[selectedColor!]?.imageUrl ?? variantItem.photos?.[0], colorId: selectedColor!, ...(variantMode === "fashion" && { sizeId: selectedSize! }), qty: variantQty, hostId: variantItem.hostId, brandId: variantItem.brandId }));
    setVariantOpen(false); setVariantItem(null); setSelectedColor(null); setSelectedSize(null); setVariantQty(1);
    if (toCheckout) goToCheckout();
  };

  const go          = (path: string) => { if (!userId) return; navigate(`/${path}/${userId}`); };
  const goToDetails  = (id: string)  => navigate(`/productdetails/${id}`);
  const goToCheckout = () => {
    if (!cartItems.length) { alert("Your cart is empty"); return; }
    navigate(`/productcheckout/${userId}`, { state: { userId, totalQty: cartItems.reduce((s: number, i: any) => s + i.qty, 0), totalPrice: cartItems.reduce((s: number, i: any) => s + i.qty * i.price, 0), items: cartItems } });
  };

  const getQty = (id: string) => cartItems.find((i: any) => i.listingId === id)?.qty || 0;

  const SINGLE_BRAND_CART_MODE = true;
  const ensureCartBrandCompat = (newBrandId?: string) => {
    if (!SINGLE_BRAND_CART_MODE) return;
    if (!cartItems.length) return;
    const currentBrandId = cartItems[0]?.brandId;
    if (currentBrandId && newBrandId && currentBrandId !== newBrandId) {
      dispatch(clearCart());
    }
  };

  const filteredProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list = filteredListings.filter((l: any) => {
      if (!l.isPublished) return false;
      if (activeProductGroup && l.productId !== activeProductGroup && l.productVariantId !== activeProductGroup) return false;
      if (q && !`${l.name ?? ""} ${l.description ?? ""}`.toLowerCase().includes(q)) return false;
      return true;
    });
    if (activeSort === "price_asc")  list = [...list].sort((a: any, b: any) => (a.rate ?? 0) - (b.rate ?? 0));
    if (activeSort === "price_desc") list = [...list].sort((a: any, b: any) => (b.rate ?? 0) - (a.rate ?? 0));
    if (activeSort === "name_asc")   list = [...list].sort((a: any, b: any) => (a.name ?? "").localeCompare(b.name ?? ""));
    if (activeSort === "newest")     list = [...list].sort((a: any, b: any) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
    if (activeSort === "default" && !activeProductGroup && !showGlobal) {
  const tierOf = (l: any) => brandTier.get(l.brandId) ?? 3;
  const sorted = [...list].sort((a, b) => tierOf(a) - tierOf(b));
  const groups = new Map<number, any[]>();
  sorted.forEach((l: any) => { const g = groups.get(l.categoryId ?? 0) ?? []; g.push(l); groups.set(l.categoryId ?? 0, g); });
  const capped: any[] = [];
  groups.forEach(g => capped.push(...g.slice(0, 100)));
  return capped;
}
return list;
}, [filteredListings, activeProductGroup, searchQuery, activeSort, showGlobal, brandTier]);



  const productsByBrand = useMemo(() => {
  const order: string[] = [];
  const map = new Map<string, any[]>();
  filteredProducts.forEach((item: any) => {
    const bId = item.brandId || "unknown";
    if (!map.has(bId)) { map.set(bId, []); order.push(bId); }
    map.get(bId)!.push(item);
  });
  return order.map(bId => ({ brandId: bId, items: map.get(bId)! }));
}, [filteredProducts]);


  /* ─── ANNOUNCEMENT BAR ───────────────────────────────────── */
  const AnnouncementBar = () => {
    const isGlobal = locationDenied || showGlobal || (!locationLoading && userCountry === null);
    if (isGlobal) return <GlobalTicker denied={locationDenied} />;
    return (
      <div style={{ background: GRAD, backgroundSize: "200% 100%", animation: "gradSpin 4s ease infinite", color: "#fff", textAlign: "center", fontSize: 10, padding: "7px 0", letterSpacing: "0.18em", fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 600 }}>
        ⚡ FREE WORLDWIDE SHIPPING ON ORDERS OVER €300
        {userCity && <span style={{ marginLeft: 16, opacity: 0.7 }}>· Near {userCity}</span>}
      </div>
    );
  };

  /* ─── HEADER FILTER ROW ──────────────────────────────────── */
  const HeaderFilterRow = () => (
    <div style={{ ...S.headerRow2, padding: "12px 16px 8px", alignItems: "center", gap: 8 }}>
      {/* Metarelm logo + wordmark */}
      <div onClick={() => navigate(`/ProductsHome/${userId}`)} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", flexShrink: 0, userSelect: "none" }}>
        <div style={{ position: "relative", display: "inline-flex" }}>
          <div style={{ position: "absolute", inset: -2, borderRadius: "50%", background: GRAD_A, backgroundSize: "300% 100%", animation: "gradSpin 3s linear infinite", zIndex: 0 }} />
          <div style={{ position: "relative", zIndex: 1, background: T.cardBg, borderRadius: "50%", padding: 2, display: "inline-flex" }}>
            <img src={logo} alt="Metarelm" style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover", display: "block" }} onError={e => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
          </div>
        </div>
        <span style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 16, fontWeight: 900, background: GRAD_A, backgroundSize: "300% 100%", animation: "gradSpin 3s linear infinite", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Metarelm</span>
      </div>

      {/* location button */}
      {locationLoading ? (
        <div style={{ display: "flex", alignItems: "center", gap: 5, background: "rgba(106,0,255,.10)", borderRadius: 999, padding: "6px 10px", flexShrink: 0 }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: T.purple, animation: "mc-pulse 1.2s infinite" }} />
          <span style={{ fontSize: 11, color: T.muted }}>Locating…</span>
        </div>
      ) : locationDenied ? (
        <button style={{ ...S.hBtn, color: T.red, borderColor: "rgba(255,59,92,.3)" }}><MapPin size={12} /></button>
      ) : (
        <button onClick={handleToggleGlobal} style={{ ...S.hBtn, color: showGlobal ? T.mutedDim : T.purple, borderColor: showGlobal ? "rgba(255,255,255,.12)" : "rgba(106,0,255,.4)" }}>
          {showGlobal ? <Globe size={12} /> : <MapPin size={12} />}
          <span style={{ display: isMobile ? "none" : "inline" }}>{showGlobal ? "Global" : userCity || userCountry || "Nearby"}</span>
        </button>
      )}

      <div style={{ flex: 1 }} />

      {!isMobile && (
        <>
          <button onClick={() => navigate(`/clientProductOrder/${userId}`)} style={S.hBtn}><RefreshCw size={13} color={T.purple} /> Buy Again</button>
          <button onClick={() => navigate(`/CommerceOnboarding/${userId}`)} style={S.hBtn}><RefreshCw size={13} color={T.purple} />Sell</button>
          <button onClick={() => go("CustomerSupport")} style={S.hBtn}><SupportAgent style={{ fontSize: 14 }} /> Support</button>
        </>
      )}

      {/* categories button */}
      <button onClick={() => isMobile ? setCatOpen(true) : setDesktopCatOpen(v => !v)}
        style={{ ...S.hBtn, background: "rgba(106,0,255,.18)", borderColor: "rgba(106,0,255,.5)", color: T.offwhite }}>
        <Menu size={14} /><span style={{ display: isMobile ? "none" : "inline" }}>Categories</span>
      </button>

      {/* sort button */}
      <button ref={sortBtnRef} onClick={toggleSort} style={{ ...S.hBtn, background: sortOpen ? "rgba(106,0,255,.25)" : "rgba(255,255,255,.06)", borderColor: sortOpen ? T.purple : "rgba(106,0,255,.3)" }}>
        <SlidersHorizontal size={12} />
        <span style={{ display: isMobile ? "none" : "inline" }}>{activeSort !== "default" ? SORT_OPTS.find(o => o.key === activeSort)?.label?.split(":")[0] : "Sort"}</span>
        {sortOpen ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
      </button>

      {locationActive && (
        <button onClick={handleToggleGlobal} style={{ ...S.hBtn, color: showGlobal ? T.mutedDim : T.blue, borderColor: showGlobal ? "rgba(255,255,255,.12)" : "rgba(0,123,255,.4)" }}>
          <Globe size={12} /><span style={{ display: isMobile ? "none" : "inline" }}>{showGlobal ? "Global" : "Local"}</span>
        </button>
      )}

      {(activeProductGroup || activeSort !== "default") && (
        <button onClick={resetFilters} style={{ ...S.hBtn, color: T.pink, borderColor: "rgba(255,0,212,.35)" }}><X size={11} /></button>
      )}

      <button onClick={() => setNavOpen(v => !v)} style={S.hBtn}>{navOpen ? <X size={18} /> : <Menu size={18} />}</button>

      {/* sort dropdown */}
      {sortOpen && sortAnchor && (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 9998 }} onClick={() => { setSortOpen(false); setSortAnchor(null); }} />
          <div style={{ ...S.sortMenu, top: sortAnchor.top, left: sortAnchor.left }}>
            {SORT_OPTS.map(opt => (
              <div key={opt.key} className={`sort-item${activeSort === opt.key ? " active" : ""}`}
                onClick={() => { setActiveSort(opt.key); setSortOpen(false); setSortAnchor(null); }}>
                {opt.label}{activeSort === opt.key && <span style={{ color: T.purple, fontWeight: 900 }}>✓</span>}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );

  /* ─── HEADER SEARCH ROW ──────────────────────────────────── */
  const HeaderSearchRow = () => (
    <div style={{ ...S.headerRow1, padding: "0 16px 12px", gap: 8 }}>
      <div style={{ ...S.searchWrap, flex: 1, borderColor: searchFocused ? T.purple : "rgba(106,0,255,.35)", boxShadow: searchFocused ? "0 0 0 3px rgba(106,0,255,.2)" : "none" }}>
        <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onFocus={() => setSearchFocused(true)} onBlur={() => setSearchFocused(false)} placeholder="Search electronics…" style={S.searchInput} />
        {searchQuery && <button onClick={() => setSearchQuery("")} style={{ background: "none", border: "none", cursor: "pointer", padding: "0 8px", color: T.muted }}><X size={14} /></button>}
        <button style={S.searchBtn}><Search size={17} color="#fff" /></button>
      </div>

      {/* notifications */}
      <div style={{ position: "relative", cursor: "pointer", flexShrink: 0 }} onClick={() => go("notifications")}>
        <Notifications style={{ fontSize: 24, color: T.purple }} />
        {unreadCount > 0 && <span style={{ position: "absolute", top: -5, right: -5, background: T.red, color: "#fff", fontSize: 9, fontWeight: 900, width: 17, height: 17, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>{unreadCount}</span>}
      </div>

      {/* cart */}
      <button onClick={goToCheckout} style={{ ...S.hBtn, position: "relative", background: cartTotal > 0 ? "rgba(106,0,255,.25)" : "rgba(255,255,255,.06)", borderColor: cartTotal > 0 ? T.purple : "rgba(106,0,255,.3)" }}>
        <ShoppingCart size={18} />
        {!isMobile && <span>{cartTotal > 0 ? `${cartTotal} items` : "Cart"}</span>}
        {cartTotal > 0 && <span style={{ position: "absolute", top: -4, right: -4, background: T.red, color: "#fff", fontSize: 9, fontWeight: 900, width: 17, height: 17, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>{cartTotal > 99 ? "99+" : cartTotal}</span>}
      </button>

      {!isMobile && (
        <>
          <button onClick={() => navigate(`/ClientDashboard/${userId}`)} style={S.hBtn}><ShoppingBag size={16} /><span>Orders</span></button>
          <button onClick={() => go("profile")} style={S.hBtn}><User size={16} /></button>
        </>
      )}
    </div>
  );

  /* ─── NAV DROPDOWN ───────────────────────────────────────── */
  const NavDropdown = () => (
    <AnimatePresence>
      {navOpen && (
        <>
          <div style={S.navOverlay as React.CSSProperties} onClick={() => setNavOpen(false)} />
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} style={S.navMenu}>
            {[
              { label: "Profile",  icon: <User size={15} />,          path: `/profile/${userId}` },
              { label: "Orders",   icon: <ShoppingBag size={15} />,    path: `/ClientDashboard/${userId}` },
              { label: "Sell",     icon: <Tag size={15} />,            path: `/CommerceOnboarding/${userId}` },
              { label: "Support",  icon: <SupportAgent style={{ fontSize: 15 }} />, path: `/CustomerSupport/${userId}` },
              { label: "Chat",     icon: <ChatBubble style={{ fontSize: 15 }} />,   path: "/MessagesPreview" },
            ].map(item => (
              <button key={item.label} className="nav-item" onClick={() => { navigate(item.path); setNavOpen(false); }}>
                <span style={{ color: T.purple }}>{item.icon}</span>{item.label}
              </button>
            ))}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  /* ─── SIDEBAR ACCORDION CATEGORY TREE ───────────────────── */
  const ElectronicsCategoryTree = () => (
    <div style={A.commerceCard}>
      <div style={A.commerceHeader}>⚡ Electronics</div>
      {electronicsCategories
        .filter((cat: any) => visibleIds.categories.has(cat.id))
        .map((cat: any) => {
          const isCatOpen = openCategories === cat.id;
          return (
            <div key={cat.id} style={A.category}>
              <div style={A.row} onClick={() => setOpenCategories(isCatOpen ? null : cat.id)}>
                {cat.icon && <cat.icon size={18} color={T.purple} />} {cat.name}
                <motion.div animate={{ rotate: isCatOpen ? 90 : 0 }} style={{ marginLeft: "auto" }}><ChevronRight size={16} color={T.mutedDim} /></motion.div>
              </div>
              <AnimatePresence>
                {isCatOpen && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
                    <div style={A.sub}>
                      {electronicsSubcategories
                        .filter((s: any) => s.parentId === cat.id && visibleIds.subs.has(s.id))
                        .map((sub: any) => {
                          const isSubOpen = openSubCategories === sub.id;
                          return (
                            <div key={sub.id}>
                              <div style={A.row} onClick={() => setOpenSubCategories(isSubOpen ? null : sub.id)}>
                                {sub.icon && <sub.icon size={16} color={T.muted} />} {sub.name}
                                <motion.div animate={{ rotate: isSubOpen ? 90 : 0 }} style={{ marginLeft: "auto" }}><ChevronRight size={14} color={T.mutedDim} /></motion.div>
                              </div>
                              <AnimatePresence>
                                {isSubOpen && (
                                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
                                    {electronicsProducts
                                      .filter((p: any) => p.parentId === sub.id && visibleIds.products.has(p.id))
                                      .map((p: any) => (
                                        <div key={p.id}
                                          style={{ ...A.productGroup, color: activeProductGroup === p.id ? T.purple : T.muted }}
                                          onClick={() => { setActiveProductGroup(p.id); closePanel(); }}>
                                          {p.icon && <p.icon size={14} />} {p.name}
                                        </div>
                                      ))}
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          );
                        })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
    </div>
  );

  /* ─── MOBILE CATEGORY BOTTOM SHEET ──────────────────────── */
  const MobileCategorySheet = () => (
    <AnimatePresence>
      {isMobile && catOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setCatOpen(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(8,6,15,.75)", zIndex: 400 }} />
          <motion.div
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 340, damping: 30 }}
            style={{ position: "fixed", bottom: 0, left: 0, right: 0, maxHeight: "82vh", borderRadius: "22px 22px 0 0", background: T.cardBg, zIndex: 401, borderTop: "1px solid rgba(106,0,255,.3)", boxShadow: "0 -20px 60px rgba(106,0,255,.18)", overflowY: "auto" }}>
            <div style={{ width: 36, height: 4, background: "rgba(106,0,255,.4)", borderRadius: 2, margin: "14px auto 0" }} />
            <div style={{ display: "flex", alignItems: "center", borderBottom: "1px solid rgba(106,0,255,.18)", padding: "12px 20px 10px" }}>
              <span style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 700, fontSize: 11, letterSpacing: "0.15em", color: T.purple }}>⚡ CATEGORIES</span>
              <div style={{ flex: 1 }} />
              <button onClick={() => setCatOpen(false)} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={15} color={T.muted} /></button>
            </div>
            <div style={{ padding: "16px 16px 32px" }}>
              <ElectronicsCategoryTree />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  /* ─── SHOP BY CATEGORY (retained structure, dark neon colors) ── */
  const ShopByCategory = () => {
    const getCatPhoto = (catId: number): string | null => {
      const l = filteredListings.find((l: any) => l.categoryId === catId && l.photos?.[0]);
      return l?.photos?.[0] ?? null;
    };

    const SHOP_CARDS = [
      { title: "Phones, Tablets, Laptops, Computers", items: [
        { id: 20000001, name: "Phones",    icon: electronicsCategories.find(c => c.id === 20000001)?.icon, color: "#007BFF" },
        { id: 20000020, name: "Tablets",   icon: electronicsCategories.find(c => c.id === 20000020)?.icon, color: "#1565C0" },
        { id: 20000021, name: "Laptops",   icon: electronicsCategories.find(c => c.id === 20000021)?.icon, color: "#6A00FF" },
        { id: 20000002, name: "Computers", icon: electronicsCategories.find(c => c.id === 20000002)?.icon, color: "#9C27B0" },
      ]},
      { title: "TVs, Audio, Gaming, Entertainment", items: [
        { id: 20000003, name: "TVs",           icon: electronicsCategories.find(c => c.id === 20000003)?.icon, color: "#FF00D4" },
        { id: 20000022, name: "Entertainment", icon: electronicsCategories.find(c => c.id === 20000022)?.icon, color: "#E91E63" },
        { id: 20000004, name: "Audio",         icon: electronicsCategories.find(c => c.id === 20000004)?.icon, color: "#007BFF" },
        { id: 20000006, name: "Gaming",        icon: electronicsCategories.find(c => c.id === 20000006)?.icon, color: "#6A00FF" },
      ]},
      { title: "Cameras, Wearables, Smart Home, GPS", items: [
        { id: 20000005, name: "Cameras",    icon: electronicsCategories.find(c => c.id === 20000005)?.icon, color: "#FF00D4" },
        { id: 20000015, name: "Wearables",  icon: electronicsCategories.find(c => c.id === 20000015)?.icon, color: "#007BFF" },
        { id: 20000024, name: "Smart Home", icon: electronicsCategories.find(c => c.id === 20000024)?.icon, color: "#6A00FF" },
        { id: 20000016, name: "GPS",        icon: electronicsCategories.find(c => c.id === 20000016)?.icon, color: "#0288D1" },
      ]},
      { title: "Appliances, Kitchen, Security, Car", items: [
        { id: 20000007, name: "Appliances", icon: electronicsCategories.find(c => c.id === 20000007)?.icon, color: "#007BFF" },
        { id: 20000008, name: "Kitchen",    icon: electronicsCategories.find(c => c.id === 20000008)?.icon, color: "#6A00FF" },
        { id: 20000014, name: "Security",   icon: electronicsCategories.find(c => c.id === 20000014)?.icon, color: "#FF00D4" },
        { id: 20000010, name: "Car",        icon: electronicsCategories.find(c => c.id === 20000010)?.icon, color: "#455A64" },
      ]},
      { title: "Office, Tools, Accessories, eBooks", items: [
        { id: 20000012, name: "Office",      icon: electronicsCategories.find(c => c.id === 20000012)?.icon, color: "#6A00FF" },
        { id: 20000011, name: "Tools",       icon: electronicsCategories.find(c => c.id === 20000011)?.icon, color: "#007BFF" },
        { id: 20000025, name: "Accessories", icon: electronicsCategories.find(c => c.id === 20000025)?.icon, color: "#FF00D4" },
        { id: 20000017, name: "eBooks",      icon: electronicsCategories.find(c => c.id === 20000017)?.icon, color: "#6A00FF" },
      ]},
    ];

    return (
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 14 }}>
          <span style={{ fontWeight: 900, fontSize: isMobile ? 16 : 18, color: T.white, letterSpacing: -0.3 }}>Shop by Category</span>
        </div>
        <div style={isMobile
          ? { display: "flex", gap: 14, overflowX: "auto", scrollSnapType: "x mandatory", WebkitOverflowScrolling: "touch" as any, scrollbarWidth: "none" as any, paddingBottom: 6 }
          : { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 18 }
        }>
          {SHOP_CARDS.map((group) => (
            <div key={group.title} style={{ flexShrink: 0, ...(isMobile ? { width: "78vw", scrollSnapAlign: "start" } : {}), background: T.cardBg, borderRadius: 20, overflow: "hidden", border: "1.5px solid rgba(106,0,255,.22)", boxShadow: "0 6px 28px rgba(106,0,255,.12)" }}>
              <div style={{ padding: isMobile ? "10px 12px 6px" : "14px 16px 8px", fontWeight: 900, fontSize: isMobile ? 12 : 13, color: T.offwhite, letterSpacing: -0.2 }}>{group.title}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: isMobile ? 4 : 8, padding: isMobile ? "0 8px 8px" : "0 10px 10px" }}>
                {group.items.map((cat) => {
                  const photo = getCatPhoto(cat.id);
                  const route = CATEGORY_ROUTES[cat.id];
                  const Icon  = cat.icon;
                  return (
                    <button key={cat.id} onClick={() => route ? navigate(route) : setActiveProductGroup(cat.id)}
                      style={{ background: "none", border: "none", cursor: "pointer", padding: 0, borderRadius: isMobile ? 10 : 14, overflow: "hidden" }}>
                      <div style={{ width: "100%", aspectRatio: "1/1", borderRadius: isMobile ? 10 : 14, overflow: "hidden", position: "relative", background: `linear-gradient(135deg,${cat.color}22,${cat.color}08)` }}>
                        {photo ? (
                          <img src={photo} alt={cat.name} style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform .35s" }}
                            onMouseEnter={e => (e.currentTarget.style.transform = "scale(1.10)")}
                            onMouseLeave={e => (e.currentTarget.style.transform = "scale(1)")} />
                        ) : (
                          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            {Icon && <Icon size={isMobile ? 26 : 34} color={cat.color} />}
                          </div>
                        )}
                        <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to top,${cat.color}EE,transparent 50%)` }} />
                        <span style={{ position: "absolute", bottom: isMobile ? 4 : 6, left: 0, right: 0, textAlign: "center", fontSize: isMobile ? 9 : 11, fontWeight: 800, color: "#fff", lineHeight: 1.2, textShadow: "0 1px 4px rgba(0,0,0,.5)" }}>{cat.name}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
              <button onClick={resetFilters} style={{ width: "100%", padding: isMobile ? "7px 12px" : "9px 16px", background: "none", border: "none", borderTop: "1px solid rgba(106,0,255,.15)", cursor: "pointer", fontSize: isMobile ? 11 : 13, fontWeight: 700, color: T.purple, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                {group.title} <ArrowRight size={isMobile ? 12 : 14} />
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  /* ─── PRODUCT CARD ─────────────────────────────────────── */
  /* Dark neon card matching phone mockup: sold badge, star rating, gradient price pill, add-to-cart */
  const ProductCard = ({ item, isMob }: { item: any; isMob: boolean }) => {
    const qty      = getQty(item.listingId);
    const price    = `${symbol || "$"}${((item.rate ?? 0) * (rate ?? 1)).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const discount = typeof item.discount === "number" && item.discount > 0 ? item.discount : null;
    const summary  = ratingSummaries[item.listingId];
    const hasRating = summary && summary.totalReviews > 0;

    return (
      <motion.div
        className="p-card"
        whileTap={{ scale: 0.96 }}
        style={{ borderRadius: 20, overflow: "hidden", background: T.cardBg, border: "1.5px solid rgba(106,0,255,.22)", cursor: "pointer", display: "flex", flexDirection: "column" }}
        onClick={() => goToDetails(item.listingId)}
      >
        {/* image area */}
        <div style={{ width: "100%", aspectRatio: "1/1", position: "relative", overflow: "hidden", background: "linear-gradient(135deg, rgba(0,123,255,0.10), rgba(106,0,255,0.06))" }}>
          {item.photos?.[0] ? (
            <motion.img src={item.photos[0]} alt={item.name} whileHover={{ scale: 1.08 }} transition={{ type: "spring", stiffness: 300, damping: 26 }}
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
          ) : (
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Package size={isMob ? 36 : 44} color={T.purple} style={{ opacity: 0.4 }} />
            </div>
          )}

          {/* dark gradient overlay */}
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(8,6,15,0.94) 0%, rgba(8,6,15,0.35) 42%, transparent 68%)", pointerEvents: "none" }} />

          {/* ── sold badge top-left ── */}
          {item.sold && item.sold > 0 && (
            <div style={{ position: "absolute", top: 8, left: 8, background: GRAD, backgroundSize: "200% 100%", animation: "gradSpin 3s ease infinite", color: "#fff", fontSize: 9, fontWeight: 800, padding: "3px 9px", borderRadius: 999, zIndex: 5, boxShadow: "0 2px 8px rgba(106,0,255,.5)" }}>
              {item.sold >= 1000 ? `${(item.sold / 1000).toFixed(0)}K` : item.sold} sold
            </div>
          )}

          {/* ── discount badge ── */}
          {discount && (
            <div style={{ position: "absolute", top: item.sold ? 30 : 8, left: 8, background: T.red, color: "#fff", fontSize: 9, fontWeight: 800, padding: "2px 8px", borderRadius: 999, zIndex: 5 }}>
              {discount}% OFF
            </div>
          )}

          {/* ── star rating pill (top, just left of add button) ── */}
          {hasRating && (
            <div style={{ position: "absolute", top: 8, right: isMob ? 50 : 56, zIndex: 6, background: "rgba(8,6,15,.85)", backdropFilter: "blur(8px)", border: "1px solid rgba(255,214,10,.35)", borderRadius: 999, padding: isMob ? "3px 7px" : "4px 9px", display: "flex", alignItems: "center", gap: 3 }}>
              <Star size={isMob ? 9 : 10} fill={T.gold} color={T.gold} />
              <span style={{ color: T.gold, fontWeight: 800, fontSize: isMob ? 9 : 10 }}>{summary!.avgRating.toFixed(1)}</span>
              <span style={{ color: T.mutedDim, fontSize: isMob ? 8 : 9 }}>({summary!.totalReviews.toLocaleString()})</span>
            </div>
          )}

          {/* ── add to cart / qty control (top-right) ── */}
          <div style={{ position: "absolute", top: 8, right: 8, zIndex: 10 }} onClick={e => e.stopPropagation()}>
            {qty > 0 ? (
              <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                style={{ display: "flex", alignItems: "center", background: "rgba(106,0,255,.92)", borderRadius: 999, padding: isMob ? "3px 5px" : "4px 6px", backdropFilter: "blur(8px)", border: "1.5px solid rgba(255,255,255,.2)", gap: 0 }}>
                <button style={{ width: isMob ? 22 : 26, height: isMob ? 22 : 26, borderRadius: "50%", border: "none", background: "rgba(255,255,255,.18)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                  onClick={e => { e.stopPropagation(); dispatch(decrementCartItem({ listingId: item.listingId })); }}>
                  <Minus size={isMob ? 8 : 10} color="#fff" />
                </button>
                <span style={{ color: "#fff", fontWeight: 900, minWidth: isMob ? 18 : 22, textAlign: "center", fontSize: isMob ? 11 : 13 }}>{qty}</span>
                <button style={{ width: isMob ? 22 : 26, height: isMob ? 22 : 26, borderRadius: "50%", border: "none", background: "rgba(255,255,255,.28)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                  onClick={e => { e.stopPropagation(); handleAddToCart(item); }}>
                  <Plus size={isMob ? 8 : 10} color="#fff" />
                </button>
              </motion.div>
            ) : (
              <motion.button
                className="add-btn"
                whileTap={{ scale: 0.88 }}
                style={{ width: isMob ? 38 : 44, height: isMob ? 38 : 44 }}
                onClick={e => { e.stopPropagation(); handleAddToCart(item); }}>
                <ShoppingCart size={isMob ? 15 : 18} color="#fff" />
              </motion.button>
            )}
          </div>

          {/* ── product name + price pill at bottom of image ── */}
          <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: isMob ? "8px 10px 10px" : "12px 14px 14px", zIndex: 3, pointerEvents: "none" }}>
            <div style={{ fontWeight: 800, fontSize: isMob ? 11 : 13, color: "#fff", lineHeight: 1.25, marginBottom: isMob ? 5 : 7, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as any, overflow: "hidden", textShadow: "0 1px 6px rgba(0,0,0,.8)", letterSpacing: -0.2 }}>
              {item.name}
            </div>
            <span className="price-pill" style={{ fontSize: isMob ? 13 : 17 }}>{price}</span>
          </div>
        </div>
      </motion.div>
    );
  };

  

  const BrandRowHeader = ({ brandId }: { brandId: string }) => {
  const partner  = brandMap.get(brandId);
  const shopName = partner?.name || "Shop";
  const shopLogo = partner?.image || logo;
  return (
    <div style={{ display:"flex", alignItems:"center", gap:10, margin:"26px 0 12px" }}>
      <div style={{ width:34, height:34, borderRadius:"50%", overflow:"hidden", flexShrink:0, border:`1px solid ${T.border}`, background:"#fff" }}>
        <img src={shopLogo} alt={shopName} style={{ width:"100%", height:"100%", objectFit:"cover" }}
          onError={e => { (e.currentTarget as HTMLImageElement).src = logo; }} />
      </div>
      <span style={{ fontWeight:900, fontSize:14, color:T.white, textDecoration:"underline", textDecorationColor:T.purple, textDecorationThickness:2, textUnderlineOffset:4 }}>
        {shopName}
      </span>
      <div style={{ flex:1 }} />
      <motion.button whileHover={{ scale:1.08 }} whileTap={{ scale:0.92 }}
        onClick={() => navigate(`/StoreHome/${brandId}`)}
        style={{ width:30, height:30, borderRadius:"50%", border:"none", background:T.cardBg2,
          boxShadow:"0 3px 10px rgba(0,0,0,.18), inset 0 1px 0 rgba(255,255,255,.6)",
          display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", flexShrink:0 }}>
        <ChevronRight size={16} color={T.purple} />
      </motion.button>
    </div>
  );
};

/* ─── RENDER PRODUCTS ────────────────────────────────────── */
const renderProducts = (isMob: boolean) => (
  <div style={{ marginBottom: 28 }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ width: 4, height: 22, borderRadius: 2, background: GRAD }} />
        <span style={{ fontWeight: 900, fontSize: 15, color: T.white }}>
          {activeProductGroup ? "Filtered" : locationDenied ? "All Products" : showGlobal ? "Global" : userCity ? `Near ${userCity}` : userCountry ?? "All Products"}
        </span>
        <span style={{ fontSize: 12, color: T.muted, background: "rgba(106,0,255,.15)", padding: "2px 9px", borderRadius: 999 }}>{filteredProducts.length}</span>
      </div>
      {locationActive && (
        <button onClick={handleToggleGlobal} style={{ display: "flex", alignItems: "center", gap: 5, background: showGlobal ? "rgba(255,255,255,.06)" : "rgba(106,0,255,.15)", borderRadius: 999, padding: "5px 13px", border: `1.5px solid ${showGlobal ? "rgba(255,255,255,.12)" : "rgba(106,0,255,.4)"}`, fontSize: 11, fontWeight: 700, color: showGlobal ? T.mutedDim : T.purple, cursor: "pointer" }}>
          {showGlobal ? <MapPin size={11} /> : <Globe size={11} />}{showGlobal ? "Show Local" : "Show Global"}
        </button>
      )}
    </div>

    {filteredProducts.length > 0 ? (
      <>
        {productsByBrand.map(group => (
          <div key={group.brandId}>
            <BrandRowHeader brandId={group.brandId} />
            <div style={isMob ? S.mobileGrid : S.grid}>
              {group.items.map((item: any) => <ProductCard key={item.listingId} item={item} isMob={isMob} />)}
            </div>
          </div>
        ))}
      </>
    ) : (
      <div style={{ textAlign: "center", padding: 56 }}>
        <div style={{ fontSize: 52, marginBottom: 14 }}>⚡</div>
        <h3 style={{ fontWeight: 900, color: T.white, marginBottom: 8 }}>{locationDenied ? "Location needed" : "No products found"}</h3>
        <p style={{ color: T.muted, marginBottom: 24, fontSize: 13 }}>{locationDenied ? "Enable location to see local products." : "No products match your current filters."}</p>
        <button onClick={() => locationDenied ? setLocationModalOpen(true) : resetFilters()}
          style={{ padding: "12px 28px", borderRadius: 999, border: "none", background: GRAD, color: "#fff", fontWeight: 800, cursor: "pointer", fontSize: 13 }}>
          {locationDenied ? "Enable Location" : "View All Products"}
        </button>
      </div>
    )}
  </div>
);

  /* ─── FEATURED BANNER ────────────────────────────────────── */
  const FeaturedBanner = () => (
    <div style={{ marginBottom: 22, padding: isMobile ? "14px 16px" : "18px 24px", background: T.cardBg, borderRadius: 18, border: "1.5px solid rgba(106,0,255,.22)", display: "flex", alignItems: "center", justifyContent: "space-between", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: -40, right: -40, width: 180, height: 180, borderRadius: "50%", background: "radial-gradient(circle, rgba(255,0,212,.14) 0%, transparent 65%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: -30, left: -20, width: 140, height: 140, borderRadius: "50%", background: "radial-gradient(circle, rgba(0,123,255,.10) 0%, transparent 65%)", pointerEvents: "none" }} />
      <div style={{ position: "relative" }}>
        <div style={{ fontWeight: 900, fontSize: isMobile ? 18 : 22, color: T.white, marginBottom: 4 }}>Featured Electronics</div>
        <div style={{ fontSize: isMobile ? 12 : 14, color: T.muted }}>Top quality tech, great prices.</div>
      </div>
      <button style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 18px", borderRadius: 999, border: "1.5px solid rgba(255,214,10,.45)", background: "rgba(255,214,10,.1)", color: T.gold, fontWeight: 800, fontSize: 12, cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap" }}>
        ⚡ Deals of the day
      </button>
    </div>
  );

  /* ─── TRUST STRIP ────────────────────────────────────────── */
  const TrustStrip = () => isMobile ? (
    <div style={{ marginTop: 12, marginBottom: 4, padding: "12px 16px", background: T.cardBg, border: "1px solid rgba(106,0,255,.18)", borderRadius: 16, display: "flex", justifyContent: "space-around", gap: 8 }}>
      {[
        { emoji: "🛡️", title: "1 Year Warranty", sub: "On all products" },
        { emoji: "🚚", title: "Free Shipping",    sub: "Over €300" },
        { emoji: "🎧", title: "24/7 Support",     sub: "We're here" },
      ].map(item => (
        <div key={item.title} style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 18 }}>{item.emoji}</span>
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, color: T.offwhite }}>{item.title}</div>
            <div style={{ fontSize: 9, color: T.muted }}>{item.sub}</div>
          </div>
        </div>
      ))}
    </div>
  ) : null;

  /* ─── VARIANT MODAL ──────────────────────────────────────── */
  const VariantModal = () => (
    <AnimatePresence>
      {variantOpen && variantItem && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={() => setVariantOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(8,6,15,.80)", zIndex: 999, display: "flex", alignItems: "flex-end", justifyContent: "center", backdropFilter: "blur(8px)" }}>
          <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 30, stiffness: 300 }}
            onClick={e => e.stopPropagation()}
            style={{ background: T.cardBg, borderRadius: "28px 28px 0 0", padding: 24, width: "100%", maxWidth: 480, maxHeight: "90vh", overflowY: "auto", border: "1px solid rgba(106,0,255,.3)" }}>
            <div style={{ width: 44, height: 4, borderRadius: 2, background: "rgba(106,0,255,.4)", margin: "0 auto 22px" }} />
            <div style={{ display: "flex", gap: 14, marginBottom: 20 }}>
              {variantItem.photos?.[0] && (
                <div style={{ width: 76, height: 76, borderRadius: 14, overflow: "hidden", background: T.surfaceBg, flexShrink: 0 }}>
                  <img src={variantItem.variants?.[previewColorId!]?.imageUrl ?? variantItem.photos[0]} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                </div>
              )}
              <div>
                <h3 style={{ margin: "0 0 4px", fontWeight: 800, fontSize: 16, color: T.white }}>{variantItem.name}</h3>
                <span style={{ background: GRAD, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", fontWeight: 900, fontSize: 18 }}>
                  {symbol || "$"}{((variantItem.rate ?? 0) * (rate ?? 1)).toFixed(2)}
                </span>
              </div>
            </div>
            {variantMode && (
              <>
                <h4 style={{ margin: "0 0 10px", fontSize: 12, fontWeight: 700, color: T.muted, textTransform: "uppercase", letterSpacing: 0.8 }}>
                  {variantMode === "beauty" ? "Select Shade" : "Select Color"}
                </h4>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
                  {Object.keys(variantItem.variants || {}).map(id => {
                    const colorId = Number(id);
                    const color   = electronicsColorAttributes.values.find((v: any) => v.id === colorId);
                    return (
                      <button key={colorId}
                        onClick={() => { setSelectedColor(colorId); if (variantMode === "fashion") setSelectedSize(null); }}
                        style={{ width: 34, height: 34, borderRadius: "50%", border: selectedColor === colorId ? `3px solid ${T.purple}` : "2px solid rgba(106,0,255,.25)", background: color?.hexCodes?.[0] ?? "#333", cursor: "pointer", transform: selectedColor === colorId ? "scale(1.15)" : "scale(1)", boxShadow: selectedColor === colorId ? `0 0 0 2px ${T.cardBg},0 0 0 4px ${T.purple}` : "none", transition: "all .15s" }} />
                    );
                  })}
                </div>
                {variantMode === "fashion" && selectedColor && (
                  <>
                    <h4 style={{ margin: "0 0 10px", fontSize: 12, fontWeight: 700, color: T.muted, textTransform: "uppercase", letterSpacing: 0.8 }}>Select Size</h4>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
                      {(variantItem.variants?.[selectedColor]?.sizeIds ?? []).map((sizeId: number) => {
                        const size = electronicsSizeAttributes.values.find((v: any) => v.id === sizeId);
                        return (
                          <button key={sizeId} onClick={() => setSelectedSize(sizeId)}
                            style={{ padding: "8px 16px", borderRadius: 10, border: `2px solid ${selectedSize === sizeId ? T.purple : "rgba(106,0,255,.25)"}`, fontWeight: 700, fontSize: 13, cursor: "pointer", background: selectedSize === sizeId ? T.purple : "transparent", color: "#fff", transition: "all .15s" }}>
                            {size?.name}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
                {selectedColor && (variantMode === "beauty" || (variantMode === "fashion" && selectedSize)) && (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 20, marginBottom: 20, background: T.surfaceBg, borderRadius: 14, padding: "12px 16px" }}>
                    <button onClick={() => setVariantQty(q => Math.max(1, q - 1))} style={{ width: 38, height: 38, borderRadius: 10, border: "2px solid rgba(106,0,255,.3)", background: "transparent", display: "grid", placeItems: "center", cursor: "pointer" }}><Minus size={14} color={T.offwhite} /></button>
                    <span style={{ fontSize: 22, fontWeight: 900, color: T.white, minWidth: 30, textAlign: "center" }}>{variantQty}</span>
                    <button onClick={() => setVariantQty(q => q + 1)} style={{ width: 38, height: 38, borderRadius: 10, border: "none", background: GRAD, display: "grid", placeItems: "center", cursor: "pointer" }}><Plus size={14} color="#fff" /></button>
                  </div>
                )}
              </>
            )}
            <div style={{ display: "flex", gap: 10 }}>
              <button disabled={!canContinue} onClick={() => handleVariantContinue(false)}
                style={{ flex: 1, padding: 14, borderRadius: 14, border: `2px solid ${canContinue ? T.purple : "rgba(106,0,255,.2)"}`, background: canContinue ? "rgba(106,0,255,.2)" : "transparent", color: canContinue ? T.offwhite : T.mutedDim, fontWeight: 800, fontSize: 14, cursor: canContinue ? "pointer" : "not-allowed" }}>
                Add to Cart
              </button>
              <button disabled={!canContinue} onClick={() => handleVariantContinue(true)}
                style={{ flex: 1, padding: 14, borderRadius: 14, border: "none", background: canContinue ? GRAD : "rgba(255,255,255,.06)", color: canContinue ? "#fff" : T.mutedDim, fontWeight: 800, fontSize: 14, cursor: canContinue ? "pointer" : "not-allowed" }}>
                Buy Now
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  /* ─── LOCATION MODAL ─────────────────────────────────────── */
  const LocationModal = () => (
    <AnimatePresence>
      {locationModalOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={() => setLocationModalOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(8,6,15,.80)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(8px)" }}>
          <motion.div initial={{ scale: 0.9, y: 40 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 40 }}
            onClick={e => e.stopPropagation()}
            style={{ width: 320, borderRadius: 20, padding: 24, background: T.cardBg, boxShadow: "0 30px 80px rgba(0,0,0,.5),0 0 0 1.5px rgba(106,0,255,.3)", textAlign: "center" }}>
            <div style={{ height: 5, borderRadius: 999, background: GRAD, marginBottom: 20 }} />
            <div style={{ fontSize: 32, marginBottom: 10 }}>📍</div>
            <h3 style={{ fontWeight: 900, marginBottom: 8, color: T.white }}>Location Disabled</h3>
            <p style={{ color: T.muted, margin: "0 0 22px", fontSize: 13, lineHeight: 1.6 }}>Enable location in your browser settings to see local products.</p>
            <button onClick={() => setLocationModalOpen(false)} style={{ padding: "11px 28px", borderRadius: 999, border: "none", background: GRAD, color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 12 }}>Got it</button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  /* ─── MOBILE BOTTOM TABS ─────────────────────────────────── */
  const BottomTabs = () => isMobile && userId ? (
    <div style={S.mobileTabs}>
      {[
        { icon: <Home size={22} />,          label: "Home",   active: true,  action: () => navigate(`/ProductsHome/${userId}`) },
        { icon: <ShoppingBasket size={22} />, label: "Stores", active: false, action: () => navigate("/MetaStores") },
        {
          label: "Cart", active: false,
          icon: (
            <div style={{ position: "relative" }}>
              <ShoppingCart size={22} />
              {cartItems.length > 0 && <span style={{ position: "absolute", top: -5, right: -5, background: T.red, color: "#fff", fontSize: 8, fontWeight: 900, width: 16, height: 16, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>{cartItems.reduce((s: number, i: any) => s + i.qty, 0)}</span>}
            </div>
          ),
          action: goToCheckout,
        },
        { icon: <ShoppingBag size={22} />, label: "Orders",  active: false, action: () => navigate(`/ClientDashboard/${userId}`) },
        { icon: <User size={22} />,         label: "Profile", active: false, action: () => navigate(`/profile/${userId}`) },
      ].map(t => (
        <motion.div key={t.label} whileTap={{ scale: 0.88 } as any} onClick={t.action}
          className={`tab-item${t.active ? " active" : ""}`}
          style={{ position: "relative" }}>
          {React.cloneElement(t.icon as any, { color: t.active ? T.purple : T.mutedDim })}
          <span>{t.label}</span>
        </motion.div>
      ))}
    </div>
  ) : null;

  /* ─── RENDER ─────────────────────────────────────────────── */
  return (
    <div style={S.page}>
      <style>{GLOBAL_CSS}</style>

      <AnnouncementBar />

      <div style={S.headerTop}>
        <HeaderFilterRow />
        <HeaderSearchRow />
      </div>

      <NavDropdown />
      <MobileCategorySheet />

      {/* DESKTOP: optional sidebar + main content */}
      {!isMobile && (
        <div style={{ ...S.desktopGrid, gridTemplateColumns: desktopCatOpen ? "300px 1fr" : "1fr" }}>
          {desktopCatOpen && (
            <aside style={A.sidebar}>
              <ElectronicsCategoryTree />
            </aside>
          )}
          <main style={{ minWidth: 0 }}>
            <FeaturedBanner />
            <ShopByCategory />
            {renderProducts(false)}
          </main>
        </div>
      )}

      {/* MOBILE */}
      {isMobile && (
        <div style={{ padding: "14px 12px 0" }}>
          <FeaturedBanner />
          <TrustStrip />
          <div style={{ marginTop: 16 }}>
            <ShopByCategory />
          </div>
          {renderProducts(true)}
        </div>
      )}

      <VariantModal />
      <LocationModal />
      <BottomTabs />
    </div>
  );
};

export default ProductsHome;
